import React from 'react'
import * as yup from 'yup'
import {
    Grid,
    Button,
    Icon,
    List,
    Popover,
} from '@softwaredev_cegeka/stix-core'
import {
    Checkbox,
    Fieldset,
    FormGroup,
    Input,
} from '@softwaredev_cegeka/stix-form'

import BaseForm from 'src/components/Forms/BaseForm'
import AsyncSelect from './AsyncSelect'
import { yupSelectRequired } from 'src/utils/validation'
import './groupForm.scss'
import { authenticatedUserStore } from 'src/stores'
import { AuthApi } from 'src/api/authApi'
import CompanySelect from 'src/components/CompanySelect'

class GroupForm extends BaseForm {
    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                name: yup
                    .string()
                    .required('Please insert the name of the group'),
                tenant: yupSelectRequired('Please select a tenant'),
                company: yupSelectRequired('Please select a company'),
                permissions: yup.array().nullable(),
                defaultValue: yup.bool(),
                isPublic: yup.bool(),
                hasAdGroups: yup.mixed().test({
                    name: 'requiredADGroups',
                    message: 'Please add an AD group or uncheck this.',
                    test: value => {
                        return !(
                            value === true &&
                            this.state.values.adGroups.length === 0
                        )
                    },
                }),
            }),
        },
    ]

    initialValues = {
        name: '',
        tenant: null,
        company: null,
        permissions: null,
        defaultValue: false,
        hasAdGroups: false,
        isPublic: false,
        adGroups: [],
    }

    get isEditMode() {
        return !!this.props.match?.params?.id
    }

    constructor(props, context) {
        super(props, context)
        this.state.displayAddBtn = true
        this.state.newAdQuery = ''
        this.state.values = { ...this.initialValues }
        if (props.match.params.id) {
            const authClient = new AuthApi(authenticatedUserStore.authToken)
            authClient.fetchGroup(props.match.params.id).then(response => {
                this.initialValues = {
                    name: response.data.name,
                    tenant: response.data.tenant_id
                        ? {
                              id: response.data.tenant_id,
                              label: response.data.tenant_name,
                              value: response.data.tenant_name,
                          }
                        : null,
                    permissions: response.data.permissions,
                    defaultValue: response.data.isDefault,
                    isPublic: response.data.isPublic,
                    hasAdGroups: !!response.data.ad_groups.length,
                    adGroups: response.data.ad_groups.map(
                        adGroup => adGroup.ad_query
                    ),
                    company: response.data.company_id
                        ? {
                              id: response.data.company_id,
                              label: response.data.company_name,
                              value: response.data.company_id,
                          }
                        : null,
                }
                this.setState({
                    values: { ...this.initialValues },
                })
            })
        }
    }

    // TODO: another call needs to be done in order to add the users to a group
    generatePayload = () => {
        const values = this.state.values
        return {
            name: values.name,
            tenant: values.tenant.id,
            permissions: values.permissions,
            isDefault: values.defaultValue,
            mapped_ad_groups: values.hasAdGroups ? values.adGroups : [],
            isPublic: values.isPublic,
            company_id: values.company.value,
        }
    }

    handleTenantRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.name,
            label: item.name,
        }))
    }

    // TODO: this needs to be changed once we have permissions in auth to
    //  match the actual response
    handlePermissionsRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.name,
            label: item.name,
        }))
    }

    handleUsersRender = response => {
        return response.data.results.map(item => ({
            id: item.id,
            value: item.first_name + ' ' + item.last_name,
            label: item.first_name + ' ' + item.last_name,
        }))
    }

    getSubmitUrl() {
        if (this.isEditMode) {
            return `/api/auth/groups/${this.props.match.params.id}`
        } else {
            return `/api/auth/tenants/${this.state.values.tenant?.id}/groups`
        }
    }

    getSubmitMethod() {
        if (this.isEditMode) {
            return 'PUT'
        } else {
            return 'POST'
        }
    }

    renderRemovePopover = item => {
        return (
            <Popover
                rootClose={true}
                placement={'left'}
                trigger={'click'}
                content={
                    <div className="modal-wrapper">
                        <div>
                            Are you sure you want to remove this AD query? The
                            change will be definitive on submit.
                        </div>
                        <div className="button-modal-wrapper">
                            <Button
                                variant={'outline-danger'}
                                onClick={() =>
                                    this.setState({
                                        values: {
                                            ...this.state.values,
                                            adGroups:
                                                this.state.values.adGroups.filter(
                                                    group => group !== item
                                                ),
                                        },
                                    })
                                }
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                }
            >
                <Button variant="naked">
                    <Icon icon={'delete'} />
                </Button>
            </Popover>
        )
    }

    renderAdGroupsList() {
        const { adGroups } = this.state.values
        return adGroups.length ? (
            <List variant={'multiColumn'}>
                {adGroups.map(item => {
                    return (
                        <List.Item key={item}>
                            <span>{item}</span>
                            {this.renderRemovePopover(item)}
                        </List.Item>
                    )
                })}
            </List>
        ) : (
            <p>
                No AD groups mapped. Add an AD group or disable the AD group
                switch.
            </p>
        )
    }

    renderAddAdGroup() {
        return this.state.displayAddBtn ? (
            <Button
                variant={'naked'}
                onClick={() =>
                    this.setState({ displayAddBtn: false, newAdQuery: '' })
                }
            >
                <Icon icon={'add'} title={'Add another AD query'} />
            </Button>
        ) : (
            <Grid.Row style={{ alignItems: 'flex-end' }}>
                <Grid.Col md={8}>
                    <Input
                        label={'New AD query'}
                        value={this.state.newAdQuery}
                        onChange={e => {
                            this.setState({ newAdQuery: e.target.value })
                        }}
                    />
                </Grid.Col>
                <Grid.Col md={2}>
                    <Button
                        block
                        variant={'secondary'}
                        disabled={!this.state.newAdQuery.length}
                        onClick={() => {
                            this.setState({
                                values: {
                                    ...this.state.values,
                                    adGroups: [
                                        ...this.state.values.adGroups,
                                        this.state.newAdQuery,
                                    ],
                                },
                                displayAddBtn: true,
                            })
                        }}
                    >
                        Add
                    </Button>
                </Grid.Col>
                <Grid.Col md={2}>
                    <Button
                        block
                        variant={'outline-secondary'}
                        onClick={() => {
                            this.setState({
                                displayAddBtn: true,
                            })
                        }}
                    >
                        Cancel
                    </Button>
                </Grid.Col>
            </Grid.Row>
        )
    }

    renderForm() {
        const companyProps = this.getCommonSelectProps('company')
        return (
            <>
                <Fieldset title="General info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('name')}
                                    label="Name"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <CompanySelect
                                    {...companyProps}
                                    required
                                    label="Company"
                                    onChange={(value, event) => {
                                        companyProps.onChange(value, event)
                                        this.setValue('tenant', null)
                                    }}
                                    disabled={this.isEditMode}
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps('tenant')}
                                    apiConfig={{
                                        url: '/api/auth/tenants',
                                        params: inputValue => ({
                                            page: 1,
                                            name_like: inputValue,
                                            sort: '+name',
                                            company_id:
                                                this.getValue('company')
                                                    ?.value ?? null,
                                        }),
                                    }}
                                    dependencies={[
                                        this.getValue('company')?.value,
                                    ]}
                                    itemRenderer={this.handleTenantRender}
                                    label="Tenant"
                                    disabled={
                                        !this.getValue('company') ||
                                        this.isEditMode
                                    }
                                    required
                                    search
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        {process.env.REACT_APP_ENABLE_PERMISSIONS_TABS ===
                        'true' ? (
                            <Grid.Col>
                                <FormGroup>
                                    <AsyncSelect
                                        {...this.getCommonSelectProps(
                                            'permissions'
                                        )}
                                        apiConfig={{
                                            url: '/api/auth/permissions',
                                            params: inputValue => ({
                                                page: 1,
                                                name_like: inputValue,
                                                sort: '+name',
                                            }),
                                        }}
                                        itemRenderer={
                                            this.handlePermissionsRender
                                        }
                                        label="Permissions"
                                        search
                                        isMulti
                                    />
                                </FormGroup>
                            </Grid.Col>
                        ) : null}
                        <Grid.Col className={'switch'}>
                            <FormGroup>
                                <Checkbox
                                    {...this.getCommonCheckboxProps(
                                        'defaultValue'
                                    )}
                                    type="switch"
                                    label="Default"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <FormGroup>
                            <Grid.Col>
                                <Checkbox
                                    {...this.getCommonCheckboxProps(
                                        'hasAdGroups'
                                    )}
                                    type={'switch'}
                                    label={'AD group'}
                                />
                            </Grid.Col>
                        </FormGroup>
                    </Grid.Row>
                    {this.state.values.hasAdGroups ? (
                        <Fieldset title={'Mapped AD groups'}>
                            <Grid.Row>
                                <Grid.Col>
                                    <FormGroup>
                                        {this.renderAdGroupsList()}
                                    </FormGroup>
                                </Grid.Col>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Col>
                                    <FormGroup>
                                        {this.renderAddAdGroup()}
                                    </FormGroup>
                                </Grid.Col>
                            </Grid.Row>
                        </Fieldset>
                    ) : null}
                    <Grid.Row>
                        <FormGroup>
                            <Grid.Col>
                                <Checkbox
                                    {...this.getCommonCheckboxProps('isPublic')}
                                    type={'switch'}
                                    label={'Public'}
                                />
                            </Grid.Col>
                        </FormGroup>
                    </Grid.Row>
                </Fieldset>
            </>
        )
    }
}

export default GroupForm
